/**
 * Structured frontend logger with backend log shipping.
 *
 * Creates component-scoped loggers that:
 * 1. Write to console.warn (the only console method allowed by lint rules)
 * 2. Batch and ship log entries to POST /api/v1/client-logs for centralized
 *    structured logging alongside the Go backend (CloudWatch/Datadog/etc.)
 *
 * Buffer + flush:
 *   - Buffer holds up to MAX_BUFFER_SIZE entries (50). Oldest entries are
 *     dropped when full to bound memory under log storms.
 *   - Each flush sends at most MAX_BATCH_SIZE entries (10) to match the
 *     backend's per-request cap. Anything left in the buffer waits for the
 *     next flush, so we never silently drop entries the backend would have
 *     truncated.
 *   - Flush fires every FLUSH_INTERVAL_MS (5s) and on visibilitychange/
 *     beforeunload for last-mile capture.
 *
 * Usage:
 *   const log = createLogger('Apollo');
 *   log.error('network timeout', { path: '/query' });
 *   log.warn('retrying request', { attempt: 2 });
 *   log.info('connected');
 */

type LogLevel = 'error' | 'warn' | 'info' | 'debug';
interface LogEntry {
  level: LogLevel;
  component: string;
  message: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
}
interface Logger {
  error(message: string, data?: Record<string, unknown>): void;
  warn(message: string, data?: Record<string, unknown>): void;
  info(message: string, data?: Record<string, unknown>): void;
  debug(message: string, data?: Record<string, unknown>): void;
}

// ---------------------------------------------------------------------------
// Log buffer & shipping
// ---------------------------------------------------------------------------

const MAX_BUFFER_SIZE = 50;
// Must match the backend's maxClientLogEntries in
// judge-api/cmd/server/cmd/client_logs.go. Sending more than this would
// cause the backend to silently truncate the tail of the batch.
const MAX_BATCH_SIZE = 10;
// Must stay under the backend's maxClientLogBodyBytes (64 KiB). We target
// 48 KiB to leave headroom for JSON framing (`{"entries":[]}` + commas) and
// for the TextEncoder's UTF-8 expansion vs JavaScript's char count. If a
// serialised batch exceeds this, flush() halves the batch and retries.
const MAX_BATCH_BYTES = 48 * 1024;
// Per-string cap applied to `message` and any metadata string value at
// enqueue time. React `componentStack` / `error.stack` payloads can
// trivially blow past 20–50 KiB on a deep tree; truncate each field so a
// single pathological entry can't consume the whole batch budget.
const MAX_FIELD_CHARS = 4 * 1024;
const FLUSH_INTERVAL_MS = 5_000;
const ENDPOINT = '/api/v1/client-logs';
let buffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let isFlushing = false;

/**
 * truncateField clips a string to MAX_FIELD_CHARS and appends a marker so the
 * backend can tell the value was truncated. Leaves short strings alone.
 */
function truncateField(s: string): string {
  if (s.length <= MAX_FIELD_CHARS) return s;
  return s.slice(0, MAX_FIELD_CHARS) + `…[truncated ${s.length - MAX_FIELD_CHARS} chars]`;
}

/**
 * normaliseMetadataValue makes a metadata value safe to place in the buffer:
 *
 *   - Strings: clipped to MAX_FIELD_CHARS (+ truncation marker).
 *   - Primitives (number, boolean, null, undefined): pass through unchanged.
 *     BigInt is coerced to a string because JSON.stringify throws on it.
 *   - Objects / arrays: attempt JSON.stringify with a circular-ref replacer;
 *     if the serialised form exceeds MAX_FIELD_CHARS, the value is replaced
 *     with a short marker instead of the full object (this is what stops a
 *     large nested object from bypassing the field-size cap); if
 *     JSON.stringify throws for any other reason we fall back to a
 *     `[unserializable: <reason>]` marker so a single bad value can't wedge
 *     the buffer on selectBatch()'s stringify call.
 */
function normaliseMetadataValue(v: unknown): unknown {
  if (typeof v === 'string') {
    return truncateField(v);
  }
  if (v === null || v === undefined || typeof v === 'number' || typeof v === 'boolean') {
    return v;
  }
  if (typeof v === 'bigint') {
    return `${v.toString()}n`;
  }
  // Objects (including arrays, Errors, DOM nodes). Pre-serialise with a
  // circular-ref replacer so selectBatch's JSON.stringify(entry) can never
  // throw on a value we let into the buffer.
  try {
    const seen = new WeakSet<object>();
    const replacer = (_key: string, value: unknown): unknown => {
      if (typeof value === 'bigint') return `${(value as bigint).toString()}n`;
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value as object)) return '[circular]';
        seen.add(value as object);
      }
      return value;
    };
    const serialised = JSON.stringify(v, replacer);
    if (serialised === undefined) {
      // Happens for pure functions, undefined at the top level, symbols.
      return '[unserializable]';
    }
    if (serialised.length > MAX_FIELD_CHARS) {
      return truncateField(serialised);
    }
    // Parse the sanitised copy so the buffer holds a plain JSON-safe
    // value (no circular refs, no Error prototypes, etc.) rather than
    // the original object. This keeps selectBatch's stringify cheap and
    // safe regardless of what the original was.
    return JSON.parse(serialised);
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    return `[unserializable: ${reason.slice(0, 80)}]`;
  }
}

/**
 * normaliseEntry applies per-field size caps before an entry enters the
 * buffer. This prevents a single pathological error (deep React stack,
 * multi-MB response body, circular object, BigInt, etc.) from pushing the
 * batch JSON over the backend's 64 KiB body limit OR from throwing in
 * selectBatch's JSON.stringify call and wedging the buffer.
 */
function normaliseEntry(entry: LogEntry): LogEntry {
  const out: LogEntry = {
    level: entry.level,
    component: entry.component,
    message: truncateField(entry.message),
    timestamp: entry.timestamp
  };
  if (entry.metadata) {
    const meta: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(entry.metadata)) {
      meta[k] = normaliseMetadataValue(v);
    }
    out.metadata = meta;
  }
  return out;
}
function enqueue(entry: LogEntry): void {
  if (buffer.length >= MAX_BUFFER_SIZE) {
    // Drop oldest entries to cap memory usage during log storms
    buffer.shift();
  }
  buffer.push(normaliseEntry(entry));
  scheduleFlush();
}
function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

/**
 * utf8ByteLength returns the serialised UTF-8 byte count of a string, which
 * is what the backend's body-size limit measures. Not necessarily equal to
 * `s.length` because JS strings are counted in UTF-16 code units.
 */
function utf8ByteLength(s: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(s).length;
  }
  // Fallback (old browsers / non-DOM environments): overestimate via Blob
  // if available, else fall back to char count * 2 for a safe upper bound.
  if (typeof Blob !== 'undefined') {
    return new Blob([s]).size;
  }
  return s.length * 2;
}

/**
 * selectBatch pulls entries off the head of the buffer subject to TWO caps:
 *   1. MAX_BATCH_SIZE entries
 *   2. MAX_BATCH_BYTES UTF-8 serialised bytes (including JSON framing)
 *
 * A single entry that's larger than MAX_BATCH_BYTES on its own is still
 * sent as a one-element batch — normaliseEntry already clips each field to
 * MAX_FIELD_CHARS so the upper bound is roughly 4 KiB * fields, well under
 * the backend limit. The single-element escape hatch means we never
 * deadlock the buffer on an oversized entry.
 */
function selectBatch(): LogEntry[] {
  const picked: LogEntry[] = [];
  let bytes = 2; // JSON framing: `{"entries":[]}` ≈ 14 bytes; 2 is a lower bound
  while (picked.length < MAX_BATCH_SIZE) {
    const entry = buffer[0];
    if (entry === undefined) break; // buffer drained

    // Defence in depth: normaliseMetadataValue already pre-serialises
    // objects, but if somehow an unserializable value slipped through
    // (new primitive types, prototype tricks) we must NEVER let the
    // exception wedge the buffer. Drop the bad entry and keep going.
    let serialised: string;
    try {
      serialised = JSON.stringify(entry);
    } catch {
      buffer.shift();
      continue;
    }
    const cost = utf8ByteLength(serialised) + 1; // +1 for the comma separator
    if (picked.length > 0 && bytes + cost > MAX_BATCH_BYTES) {
      // This entry would overflow the batch — stop and flush what we have.
      break;
    }
    picked.push(entry);
    bytes += cost;
    buffer.shift();
  }
  return picked;
}

/** Flush sends one batch (bounded by entry count AND byte budget) to the backend. Exported for testing. */
export async function flush(): Promise<void> {
  if (buffer.length === 0 || isFlushing) return;
  const entries = selectBatch();
  if (entries.length === 0) return;
  isFlushing = true;
  try {
    const resp = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        entries
      }),
      // Use keepalive so logs survive page unload
      keepalive: true
    });
    // If the backend rejects, don't retry — the entries were best-effort
    if (!resp.ok) {
      // eslint-disable-next-line judge-custom/no-bare-console
      console.warn('[logger] backend rejected client logs', resp.status);
    }
  } catch (error) {
    // Network failure — drop the batch; we don't retry, to avoid the logger
    // itself causing errors or infinite retry loops. Report once via the same
    // console sink used above (the logger cannot route its own failures).
    // eslint-disable-next-line judge-custom/no-bare-console -- logger sink cannot log through itself
    console.warn('[logger] failed to ship client logs', String(error));
  } finally {
    isFlushing = false;
  }

  // If more entries arrived (or were left over after the cap), schedule the
  // next flush so we drain the buffer rather than waiting another 5s.
  if (buffer.length > 0) {
    scheduleFlush();
  }
}

/**
 * flushAll drains the entire buffer by calling flush() repeatedly until the
 * buffer is empty or too many iterations pass. Used from unload hooks where
 * the 5-second flush timer cannot fire anymore — if we called flush() once,
 * anything past MAX_BATCH_SIZE / MAX_BATCH_BYTES would be silently lost.
 *
 * `keepalive: true` on the underlying fetch lets browsers service the POST
 * after the page has started unloading, so multiple drained batches can all
 * be shipped. Most browsers cap total keepalive body size at 64 KiB per
 * origin, so at most ~6-7 full batches can go out — that's enough for the
 * tail of a burst but not truly unbounded. The buffer cap of
 * MAX_BUFFER_SIZE (50 entries) ensures we don't try to send more than this.
 */
export async function flushAll(): Promise<void> {
  // Hard cap on iterations so a wedged flush() can't spin forever in the
  // brief unload window. MAX_BUFFER_SIZE / MAX_BATCH_SIZE plus one is the
  // theoretical maximum number of sequential batches needed.
  const maxIterations = Math.ceil(MAX_BUFFER_SIZE / MAX_BATCH_SIZE) + 1;
  for (let i = 0; i < maxIterations && buffer.length > 0; i++) {
    const prevLen = buffer.length;
    await flush();
    if (buffer.length === prevLen) {
      // flush() made no progress (probably isFlushing raced). Bail.
      break;
    }
  }
}

// Flush on page unload so we don't lose the last batch. Use flushAll so the
// unload hook drains EVERY buffered entry, not just the first batch — without
// this, any burst beyond MAX_BATCH_SIZE / MAX_BATCH_BYTES got left behind a
// 5-second scheduleFlush timer that will never fire after the page unloads.
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushAll();
    }
  });
  window.addEventListener('beforeunload', () => {
    void flushAll();
  });
}

// ---------------------------------------------------------------------------
// Logger factory
// ---------------------------------------------------------------------------

export function createLogger(component: string): Logger {
  const prefix = `[${component}]`;
  const log = (level: LogLevel, message: string, data?: Record<string, unknown>): void => {
    // Write to console for local devtools visibility
    // eslint-disable-next-line judge-custom/no-bare-console
    console.warn(prefix, level.toUpperCase(), message, data ?? '');

    // Enqueue for backend shipping
    enqueue({
      level,
      component,
      message,
      metadata: data,
      timestamp: new Date().toISOString()
    });
  };
  return {
    error: (message, data) => log('error', message, data),
    warn: (message, data) => log('warn', message, data),
    info: (message, data) => log('info', message, data),
    debug: (message, data) => log('debug', message, data)
  };
}

// ---------------------------------------------------------------------------
// Test helpers — reset internal state between tests
// ---------------------------------------------------------------------------

/** @internal — only for test use */
export function _resetForTesting(): void {
  buffer = [];
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  isFlushing = false;
}

/** @internal — only for test use */
export function _getBuffer(): readonly LogEntry[] {
  return buffer;
}